import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useEffect, useState } from 'react'

import FormGroup from '@/core/components/FormGroup'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import type { IClient, IClientPayload } from '@/core/services/clients'
import { formatDateTime } from '@/core/constants/times'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Icon } from '@/common/components/Icon'

export interface IFormValues {
  id?: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  newPassword?: string
  referralCode?: string
  createdDate?: string
  lastActivity?: string
  ordersSize?: number
  location?: string
  scanTicketsPermission: boolean
}

interface UserInfoFormProps {
  client?: IClient
  hasEditPermission: boolean
  onSubmit: (data: IClientPayload) => Promise<void>
  onSendEmail: () => Promise<void>
}

const schemaObject = {
  id: yup.string().optional(),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  phoneNumber: yup.string().required('Phone Number is required'),
  email: yup.string().email().required('Email is required'),
  newPassword: yup.string().optional(),
  referralCode: yup.string().optional(),
  createdDate: yup.string().optional(),
  lastActivity: yup.string().optional(),
  ordersSize: yup.number().optional(),
  location: yup.string().optional(),
  scanTicketsPermission: yup.boolean().required(),
}

const schema = yup.object().shape(schemaObject)

export const UserInfoForm: React.FC<UserInfoFormProps> = ({
  client,
  hasEditPermission,
  onSubmit,
  onSendEmail,
}) => {
  const [phoneNumberEnabled, setPhoneNumberEnabled] = useState(false)

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
    defaultValues: {
      id: client?.id,
      firstName: client?.firstName || '',
      lastName: client?.lastName || '',
      phoneNumber: client?.phoneNumber || '',
      email: client?.email || '',
      referralCode: client?.referralCode || '',
      createdDate: client?.createdDate
        ? formatDateTime(client.createdDate)
        : '',
      lastActivity: client?.lastActivity
        ? formatDateTime(client.lastActivity)
        : '',
      ordersSize: client?.ordersSize || 0,
      location: client?.location || '',
      scanTicketsPermission: client?.scanTicketsPermission || false,
    },
  })

  useEffect(() => {
    if (client) {
      setValue('id', client.id)
      setValue('firstName', client.firstName || '')
      setValue('lastName', client.lastName || '')
      setValue('phoneNumber', client.phoneNumber || '')
      setValue('email', client.email || '')
    }
  }, [client, setValue])

  const email = watch('email')

  const submit = async (data: IFormValues) => {
    const payload: IClientPayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      email: data.email,
      scanTicketsPermission: data.scanTicketsPermission,
    }
    if (data.newPassword != '') {
      payload.newPassword = data.newPassword
    }
    await onSubmit(payload)
  }

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit(submit)}>
        <div className="grid grid-cols-3 gap-2.5">
          <FormGroup label="ID">
            <Input
              isInvalid={!!errors?.id?.message}
              {...register('id')}
              disabled
            />
          </FormGroup>
          <FormGroup label="First Name" error={errors?.firstName?.message}>
            <Input
              isInvalid={!!errors?.firstName?.message}
              {...register('firstName')}
            />
          </FormGroup>
          <FormGroup label="Last Name" error={errors?.lastName?.message}>
            <Input
              isInvalid={!!errors?.lastName?.message}
              {...register('lastName')}
            />
          </FormGroup>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-[30px]">
          <FormGroup label="Phone Number" error={errors?.phoneNumber?.message}>
            <div className="flex w-full flex-col gap-2">
              <Input
                className="w-full"
                disabled={!phoneNumberEnabled}
                isInvalid={!!errors?.phoneNumber?.message}
                {...register('phoneNumber')}
              />
              <Button
                variant="secondary"
                disabled={!hasEditPermission}
                size="sm"
                type="button"
                onClick={() => setPhoneNumberEnabled(!phoneNumberEnabled)}
              >
                Change Number
              </Button>
            </div>
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <div className="flex flex-col w-full gap-2">
              <Input
                isInvalid={!!errors?.email?.message}
                {...register('email')}
                autoComplete="new-password"
              />
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={onSendEmail}
                disabled={!hasEditPermission || email !== client?.email}
              >
                Send Confirmation Email
              </Button>
            </div>
          </FormGroup>
        </div>
        <div className="flex flex-col gap-3 mt-[30px]">
          <FormGroup label="Setup NEW Password">
            <Input
              isInvalid={!!errors?.newPassword?.message}
              {...register('newPassword')}
              type="password"
              autoComplete="new-password"
            />
          </FormGroup>
          <div className="border border-accent-4 bg-monochrome-3-60 px-4 py-3 rounded-2xl flex items-center gap-2">
            <Icon
              icon="warning"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="text-accent-4"
            />
            Attention, the password will be changed
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <FormGroup label="Referral Code">
              <div className="flex w-full gap-2 relative">
                <Input
                  disabled
                  isInvalid={!!errors?.referralCode?.message}
                  {...register('referralCode')}
                />
                <Button
                  className="absolute right-[2px] top-[2px]"
                  variant="secondary"
                  size="sm"
                  type="button"
                >
                  Copy
                  <Icon
                    icon="copy"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="text-monochrome-1"
                  />
                </Button>
              </div>
            </FormGroup>
            <FormGroup label="Created Date">
              <Input
                type="text"
                disabled
                value={watch('createdDate')}
                placeholder="Created Date"
              />
            </FormGroup>
            <FormGroup label="Last Activity Date">
              <Input
                type="text"
                disabled
                value={watch('lastActivity')}
                placeholder="Last Activity Date"
              />
            </FormGroup>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <FormGroup label="Number of Orders">
              <Input
                isInvalid={!!errors?.ordersSize?.message}
                {...register('ordersSize')}
                disabled
                type="number"
                placeholder="Number of Orders"
              />
            </FormGroup>
            <FormGroup label="User Location">
              <Input
                isInvalid={!!errors?.location?.message}
                {...register('location')}
                disabled
                placeholder="User Location"
              />
            </FormGroup>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="scanTicketsPermission"
              {...register('scanTicketsPermission')}
            />
            <label
              htmlFor="scanTicketsPermission"
              className="text-sm cursor-pointer"
            >
              Can scan tickets
            </label>
          </div>
        </div>
        {hasEditPermission && (
          <>
            <hr className="my-4 border-monochrome-120" />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                className="min-w-[154px]"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="min-w-[154px]"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
